/* eslint-disable react/jsx-key */
import { useEffect, useRef, useState } from "react";
import { Info } from "react-feather";
import Visibility from "visibilityjs";
import { useAppSelector } from "@axvdex/state";
import useLanguage from "@axvdex/hooks/useLanguage";
import { SaleStatus } from "@axvdex/types/solana/outbid";

type TimeLeftComponentProps = {
  status: SaleStatus;
  startTimestamp: number;
  whitelistDuration: number;
  duration: number;
  bonusDuration: number;
  endTimestamp: number;
};

export const TimeLeftComponent = ({
  status,
  startTimestamp,
  whitelistDuration,
  duration,
  bonusDuration,
  endTimestamp,
}: TimeLeftComponentProps) => {
  const { i18 } = useLanguage();
  const outbidServerTime = useAppSelector(state => state.outbid.outbidServerTime);
  const [countdownElement, setCountdownElement] = useState(null);

  const [curTime, setCurTime] = useState(new Date().getTime() / 1000);
  const [curVisibilityState, setCurVisibilityState] = useState(true);
  const curTimeRef = useRef(new Date().getTime() / 1000);
  const curVisibilityStateRef = useRef(true);

  useEffect(() => {
    curTimeRef.current = curTime;
  });

  useEffect(() => {
    curVisibilityStateRef.current = curVisibilityState;
  });

  useEffect(() => {
    getCurrentProgressForBar(status, startTimestamp, whitelistDuration, duration, bonusDuration, endTimestamp);
    const interval = setInterval(() => {
      getCurrentProgressForBar(status, startTimestamp, whitelistDuration, duration, bonusDuration, endTimestamp);
      setCurTime(curTimeRef.current + 1);
      if (false === curVisibilityStateRef.current && "visible" == Visibility.state()) {
        setCurTime(new Date().getTime() / 1000);
        setCurVisibilityState(true);
      } else if ("hidden" == Visibility.state()) {
        setCurVisibilityState(false);
      }
    }, 1000);
    return () => clearInterval(interval);
  }, [status, startTimestamp, whitelistDuration, duration, bonusDuration, endTimestamp]);

  useEffect(() => {
    if (outbidServerTime) setCurTime(outbidServerTime / 1000);
  }, [outbidServerTime]);

  const getTime = duration => {
    return {
      hours: Math.floor(duration / (60 * 60)),
      minutes: Math.floor((duration / 1 / 60) % 60),
      seconds: Math.floor((duration / 1) % 60),
    };
  };

  const getCurrentProgressForBar = (
    status: SaleStatus,
    startTimestamp: number,
    whitelistDuration: number,
    duration: number,
    bonusDuration: number,
    endTimestamp: number
  ) => {
    const curDate = curTimeRef.current;

    const defaultTimeLeftComponent = (
      <div className="inlineFlexbox">
        <div style={{ alignSelf: "center" }}>
          <div className="inlineFlexbox">
            <Info data-tooltip-id="tooltip_timeInfo" style={{ width: "16px", height: "16px" }} />
            <p className="textGrey" style={{ marginBottom: "0px", textAlign: "right" }}>
              {i18("Time left", "sales.timeLeft")}
            </p>
          </div>
          <div style={{ fontSize: "1.5em" }}>--:--:--</div>
        </div>
      </div>
    );
    if ("WaitingToStart" === status) {
      const countdownTime = getTime(startTimestamp - curDate > 0 ? startTimestamp - curDate : 0);
      setCountdownElement(
        <div className="inlineFlexbox">
          <div style={{ alignSelf: "center" }}>
            <div className="inlineFlexbox">
              <Info data-tooltip-id="tooltip_timeInfo" style={{ width: "16px", height: "16px" }} />
              <p className="textGrey" style={{ marginBottom: "0px", textAlign: "right" }}>
                {i18("Time left", "sales.timeLeft")}
              </p>
            </div>
            <div style={{ fontSize: "1.5em" }}>
              {String(countdownTime.hours).padStart(2, "0")}:{String(countdownTime.minutes).padStart(2, "0")}:
              {String(countdownTime.seconds).padStart(2, "0")}
            </div>
          </div>
        </div>
      );
    }
    if ("WhitelistBidding" === status) {
      const curTimePast = curDate - startTimestamp;
      const totalDuration = whitelistDuration;
      const countdownTime = getTime(totalDuration - curTimePast > 0 ? totalDuration - curTimePast : 0);
      setCountdownElement(
        <div className="inlineFlexbox">
          <div style={{ alignSelf: "center" }}>
            <div className="inlineFlexbox">
              <Info data-tooltip-id="tooltip_timeInfo" style={{ width: "16px", height: "16px" }} />
              <p className="textGrey" style={{ marginBottom: "0px", textAlign: "right" }}>
                {i18("Time left", "sales.timeLeft")}
              </p>
            </div>
            <div style={{ fontSize: "1.5em" }}>
              {String(countdownTime.hours).padStart(2, "0")}:{String(countdownTime.minutes).padStart(2, "0")}:
              {String(countdownTime.seconds).padStart(2, "0")}
            </div>
          </div>
        </div>
      );
    }
    if ("Bidding" === status) {
      const curTimePast = curDate - (startTimestamp + whitelistDuration);
      const totalDuration = startTimestamp + duration - bonusDuration - (startTimestamp + whitelistDuration);
      const countdownTime = getTime(totalDuration - curTimePast > 0 ? totalDuration - curTimePast : 0);
      setCountdownElement(
        <div className="inlineFlexbox ">
          <div style={{ alignSelf: "center" }}>
            <div className="inlineFlexbox">
              <Info data-tooltip-id="tooltip_timeInfo" style={{ width: "16px", height: "16px" }} />
              <p className="textGrey" style={{ marginBottom: "0px", textAlign: "right" }}>
                {i18("Time left", "sales.timeLeft")}
              </p>
            </div>
            <div style={{ fontSize: "1.5em" }}>
              {String(countdownTime.hours).padStart(2, "0")}:{String(countdownTime.minutes).padStart(2, "0")}:
              {String(countdownTime.seconds).padStart(2, "0")}
            </div>
          </div>
        </div>
      );
    }
    if ("BonusBidding" === status) {
      const curTimePast = curDate - (endTimestamp - bonusDuration);
      const totalDuration = endTimestamp - (endTimestamp - bonusDuration);
      const countdownTime = getTime(totalDuration - curTimePast > 0 ? totalDuration - curTimePast : 0);
      setCountdownElement(
        <div className="inlineFlexbox">
          <div style={{ alignSelf: "center" }}>
            <div className="inlineFlexbox">
              <Info data-tooltip-id="tooltip_timeInfo" style={{ width: "1em", height: "1em" }} />
              <p className="textGrey" style={{ marginBottom: "0px", textAlign: "right" }}>
                {i18("Time left", "sales.timeLeft")}
              </p>
            </div>
            <div style={{ fontSize: "1.5em", color: "#f6590b" }}>
              {String(countdownTime.hours).padStart(2, "0")}:{String(countdownTime.minutes).padStart(2, "0")}:
              {String(countdownTime.seconds).padStart(2, "0")}
            </div>
          </div>
        </div>
      );
    }
    if (["EndedSoldOut", "EndedNotSoldOut", "EndedBonusTimeSoldOut"].includes(status)) {
      setCountdownElement(defaultTimeLeftComponent);
    }
  };

  return countdownElement;
};
